/** @format */

import React, { ChangeEvent, useRef, useState } from "react";
import AuthFlag from "../../auth/AuthFlag";
import { ReactComponent as ExclamationTriangle } from "bootstrap-icons/icons/exclamation-triangle.svg";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import {
  humanFileSize,
  storyboardToJSON,
  getJsonReturn,
} from "./BLUploadService";
import { useAppSelector } from "../../../app/hooks";
import { getUserToken } from "../../auth/authSlice";
import { useCreateResourceMutation } from "../../../app/services/resourcesApi";
import CreateResourceForm, {
  ResourceForm,
} from "../createResourceForm/CreateResourceForm";
import { BLResource } from "../BLResourceTypes";
import { fundedBy, QualificationId } from "../../../types";
import { subjectNameToId } from "../../../utils/functions";
import { useGetSubjectsQuery } from "../../../app/services/subjectApi";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useLocale } from "../../../i18n/hooks";
import HelpLink from "../../helpLink/HelpLink";
import UploadConfirmationModal from "./UploadConfirmationModal";

interface HTMLFileInputElement extends HTMLInputElement {
  files: FileList;
}

// NOTE: this could be an enum without strings?
enum stageEnum {
  StageOne = "StageOne",
  StageTwo = "StageTwo",
  StageThree = "StageThree",
  StageFour = "StageFour",
  StageFive = "StageFive",
  StageFiveError = "StageFiveError",
}

const jsonFileType = "application/json";
const docxFileType =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const UploadBL: React.FC = () => {
  const locale = useLocale();
  const { t: _ } = useTranslation();
  const fileInputRef = useRef<HTMLFileInputElement>(null);
  const [stage, setStage] = useState<stageEnum>(stageEnum.StageOne); // Always start at stage one
  const [uploadedFile, setUploadedFile] = useState<File>(); // stores the currently uploaded file
  const userToken = useAppSelector(getUserToken);
  const [createResource] = useCreateResourceMutation();
  const [intitalCreateResourceForm, setIntitalCreateResourceForm] =
    useState<ResourceForm>();
  const [saveData, setSaveData] = useState<BLResource>();
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [resourceURL, setResourceURL] = useState<string>(""); // used to display the link for the final resource

  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [finalData, setFinalData] = useState<BLResource>();

  // used to convert subject name to id
  const { data: subjects } = useGetSubjectsQuery();

  /**
   * @param devError the error msg to be used to debug the code
   *
   * used when an error is not the users fault and is a problem with the application
   */
  const devErrorMsg = (devError: string): string => {
    return (
      _(
        "An error has occured please reach out to the development team with this code: ",
      ) +
      "'" +
      devError +
      "'"
    );
  };

  /**
   * checks that a file has been uploaded and is a JSON or docx
   * if JSON, handle the JSON
   * if docx, handle the storyboard
   * move to stage two
   */
  const submitDocument = () => {
    if (!uploadedFile) {
      setErrorMsg(devErrorMsg("no file uploaded in submitDocument"));
      return;
    }

    if (uploadedFile.type === jsonFileType) {
      setStage(stageEnum.StageTwo);
      setErrorMsg("");
      handleJSON();
      return;
    } else if (uploadedFile.type === docxFileType) {
      setStage(stageEnum.StageTwo);
      setErrorMsg("");
      handleStoryboard();
      return;
    } else {
      setErrorMsg(
        _("incorrect file type") +
          " '" +
          uploadedFile.name.substring(uploadedFile.name.indexOf(".")) +
          "'",
      );
      return;
    }
  };

  /**
   * reads the uploadedFile as JSON and sends the JSON data to final processing (finalJSON)
   */
  const handleJSON = () => {
    if (!uploadedFile) {
      setErrorMsg(devErrorMsg("uploadedFile file is undefined in handleJSON"));
      return;
    }

    if (uploadedFile.type !== jsonFileType) {
      setErrorMsg(devErrorMsg("wrong file type in handleJSON"));
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = () => {
      const _json = JSON.parse(fileReader.result as string);
      finalJSON(_json);
    };

    fileReader.readAsText(uploadedFile);
  };

  /**
   * ensures the user has a token (is logged in)
   * with the uploaded file, send a request to the server to convert the storyboard to JSON
   * sends the JSON data to final processing
   */
  const handleStoryboard = async () => {
    if (!userToken) {
      setErrorMsg(
        devErrorMsg("uploadedFile file is undefined in handleStoryboard"),
      );
      return;
    }

    // This should have already been asserted as defined
    if (!uploadedFile) {
      setErrorMsg(devErrorMsg("no file uploaded in handleStoryboard"));
      return;
    }

    if (uploadedFile.type !== docxFileType) {
      setErrorMsg(devErrorMsg("wrong file type in handleStoryboard"));
      return;
    }

    storyboardToJSON(userToken, uploadedFile)
      .then((value) => {
        let data = value as getJsonReturn;
        finalJSON(data.resource as BLResource);
      })
      .catch((error) => {
        setErrorMsg(devErrorMsg(error));
      });
  };

  /**
   * @param data a json object handled as a BLResource
   *
   * using data, populate the createResourceForm and save it to the saveData state
   * move to stage three
   */
  const finalJSON = (data: BLResource) => {
    setSaveData(data);

    const subjectId = subjectNameToId(
      data.subject.en !== "" ? data.subject.en : data.subject.cy,
      subjects?.subjects,
    );

    let fundedBy: fundedBy = "N/A";
    if (data.enhancedLearning || data.fundedBy === "WelshGov")
      fundedBy = "WelshGov";
    if (data.adnoddFunded || data.fundedBy === "Adnodd") fundedBy = "Adnodd";

    // Populate the createResourceForm with data from the JSON and empty defaults
    setIntitalCreateResourceForm({
      code: data.code,
      qualification: "",
      levels: [],
      subject: {
        id: subjectId,
        title: {
          en: subjectId === "" ? "" : data.subject.en,
          cy: subjectId === "" ? "" : data.subject.cy,
        },
      },
      specItem: "",
      acknowledgements: "",
      fundedBy: fundedBy,
      author: "",
      resourceTitle: {
        // Only save the resource title of the current locale.
        // Otherwise the user may have a hidden resourceTitle for the other locale.
        en: !!data.resourceTitle.en ? data.resourceTitle.en : data.code,
        cy: !!data.resourceTitle.cy ? data.resourceTitle.cy : data.code,
      },
      branding: "cbac&wjec",
    });

    setErrorMsg("");
    setStage(stageEnum.StageThree);
  };

  /**
   * @param formData the data entered by a user about a resource. Should probs come from the createResourceForm
   *
   * using the formData and the previously saved data, send a request to create a new resource
   * move to stage four
   */
  const uploadFinal = (formData: ResourceForm) => {
    if (!saveData) {
      setErrorMsg(devErrorMsg("no save data in uploadFinal"));
      return;
    }

    const finalData: BLResource = {
      subjectId: formData.subject?.id ?? "",
      levels: formData.levels ?? [],
      subject: {
        en: formData.subject?.title.en ?? "",
        cy: formData.subject?.title.cy ?? "",
      },
      code: formData.code,
      qualification: formData.qualification as QualificationId,
      fundedBy: formData.fundedBy,
      acknowledgements: formData.acknowledgements,
      specItem: formData.specItem,
      authors: formData.author,
      resourceTitle: {
        en: !!formData.resourceTitle.en
          ? formData.resourceTitle.en
          : formData.code,
        cy: !!formData.resourceTitle.cy
          ? formData.resourceTitle.cy
          : formData.code,
      },

      lessons: saveData.lessons,
      branding: formData.branding,
    };

    setFinalData(finalData);
    setShowConfirmationModal(true)
  };

  const acceptConfirmation = () => {
    if (!finalData) {
      setErrorMsg(devErrorMsg("no final data in acceptConfirmation"));
      return;
    }

    // Show loading screen
    setErrorMsg("");
    setStage(stageEnum.StageFour);

    (async () => {
      await createResource({ resource: finalData })
        .unwrap()
        .then(() => {
          // Resource has been successfully created
          setResourceURL(
            `/${finalData.subject[locale]}/r/${
              finalData.code
            }/BL/${finalData.qualification.toUpperCase()}`,
          );
          setErrorMsg("");
          setStage(stageEnum.StageFive);
        })
        .catch((error) => {
          setStage(stageEnum.StageFiveError);
          if (error.data.message) {
            setErrorMsg(devErrorMsg(error.data.message));
          }
        });
    })();
  }

  return (
    <div className="position-relative">
      <AuthFlag authGroup={["wjec", "admin"]} fallback={<h1>{_("Unauthorised")}</h1>}>
        <HelpLink
          position="top-end"
          className="help-link pe-0"
          locationId="new-resource"
        />
        {errorMsg !== "" && (
          <Alert variant="danger">
            <ExclamationTriangle className="text-danger" /> {errorMsg}
          </Alert>
        )}

        {stage === stageEnum.StageOne && (
          <div data-testid="StageOne">
            <p className="mb-2">
              {_(
                "Please upload a JSON or Word document here to create a resource",
              )}
            </p>
            {
              //TODO: this doesn't translate
            }
            <Form.Control
              data-testid="UploadFile"
              type="file"
              id="upload-document"
              aria-label="Upload document input"
              className="mb-4"
              onChange={(e: ChangeEvent<HTMLFileInputElement>) =>
                setUploadedFile(e.target.files[0])
              }
              accept={jsonFileType + "," + docxFileType}
              ref={fileInputRef}
            />
            <Button disabled={!uploadedFile} onClick={() => submitDocument()}>
              {_("Submit")}
            </Button>
          </div>
        )}

        {stage === stageEnum.StageTwo && (
          <div data-testid="StageTwo">
            <p>{_("Processing uploaded file")}</p>
            <Spinner animation="border" className="me-3 mb-2" />
            {
              // check for undefined. uploadedFile should deffo not be undefined at this point
            }
            {uploadedFile && (
              <div className="d-flex gap-4">
                <div className="d-flex gap-2">
                  <span>
                    <strong>{_("File Size")}</strong>
                  </span>
                  {humanFileSize(uploadedFile.size, false, 1)}
                </div>
                <div className="d-flex gap-2">
                  <span>
                    <strong>{_("Last modified")}</strong>
                  </span>
                  {new Date(uploadedFile.lastModified).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        )}

        {stage === stageEnum.StageThree && (
          <>
            <div data-testid="StageThree">
              <CreateResourceForm
                buttonText={_("Create Resource")}
                uploadFinal={uploadFinal}
                propDefaultResourceForm={intitalCreateResourceForm}
              />
            </div>
            
            <UploadConfirmationModal 
              keyData={
                {
                  code: finalData?.code,
                  subject: {
                    id: finalData?.subjectId,
                    title: finalData?.subject,
                  },
                  type: "BL",
                  qualification: finalData?.qualification,
                }
              } 
              show={showConfirmationModal}
              onHide={() => setShowConfirmationModal(false)}
              onAccept={acceptConfirmation}
            />
          </>
        )}

        {stage === stageEnum.StageFour && (
          <div data-testid="StageFour">
            <p>{_("Uploading Resource")}</p>
            <Spinner animation="border" className="me-3 mb-2" />
          </div>
        )}

        {stage === stageEnum.StageFive && (
          <div data-testid="StageFive">
            <h3>{_("Resource Created")}</h3>
            {resourceURL !== "" && (
              <Link className="btn btn-primary" to={resourceURL}>
                {_("View resource")}
              </Link>
            )}
          </div>
        )}

        {stage === stageEnum.StageFiveError && (
          <div data-testid="StageFive">
            <Alert variant="danger">
              <h3>{_("An error has occured while creating this resource")}</h3>
              <Button
                className="mt-2 btn btn-danger"
                onClick={() => {
                  setErrorMsg("");
                  setStage(stageEnum.StageThree);
                }}
              >
                {_("Back")}
              </Button>
              <Link
                className="btn btn-danger mt-2 ms-4"
                to="/upload-bl?type=BL"
              >
                {_("Restart")}
              </Link>
            </Alert>
          </div>
        )}
      </AuthFlag>
    </div>
  );
};

export default UploadBL;
